import { useTranslation } from 'react-i18next';
import './contracts-table.component.scss';
import { INVOZIO_BLOB_BASE } from '../constant/video-url-import.constant';

export interface Contract {
    id: string;
    // contractNo: string;
    document: string;
    duration: string;
    fileUrl?: string;
}

interface ContractsTableProps {
    contracts: Contract[];
    onDownload?: (contractId: string) => void;
    onDelete?: (contractId: string) => void;
}

// Cross-origin hosts (e.g. Azure Blob Storage) typically don't send CORS
// headers, so fetch()-ing the bytes would be blocked by the browser. This
// just navigates to the file directly instead, letting the browser handle it.
export const openExternalFile = (fileURL: string, fileName?: string) => {
    const link = document.createElement("a");
    link.href = fileURL;
    link.download = fileName || fileURL.split("/").pop() || "download";
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.click();
};

export const downloadFile = async (fileURL: string, fileName?: string) => {
    try {
        const response = await fetch(fileURL, { method: "GET" });
        if (!response.ok) throw new Error("Failed to download file");

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = fileName || fileURL.split("/").pop() || "download";

        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

        if (isIOS || isSafari) {
            window.location.href = fileURL; // open in new tab
        } else {
            link.click(); // trigger download
        }

        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error("Download failed:", error);
    }
};

export const ContractsTable = ({
    onDownload,
    onDelete,
}: ContractsTableProps) => {

    const workAgrementSwedish = `${INVOZIO_BLOB_BASE}/invozio_avtal.pdf`;
    const workAgrementEnglish = `${INVOZIO_BLOB_BASE}/invozio_agreement.pdf`;

    const { t, i18n } = useTranslation();

    const language = i18n.language;
    const contracts: Contract[] = [
        {
          id: '1',
          document: 'GENERAL_DOCUMENT',
          duration: '12/05/2023 - 11/09/2024',
        },
    ];

    const handleDownload = (contract: Contract) => {
        const fileUrl = language === 'sv-se' ? workAgrementSwedish : workAgrementEnglish;
        openExternalFile(fileUrl, `${t(contract.document)}.pdf`);
        onDownload?.(contract.id);
    };

    return (
        <div className="contracts-table">
            <div className="contracts-table__wrapper">
                <table className="contracts-table__table">
                    <thead className="contracts-table__thead">
                        <tr className="contracts-table__header-row">
                            {/* <th className="contracts-table__header-cell contracts-table__header-cell--contract-no">
                                {t('CONTRACT_NO') || 'Contract No.'}
                            </th> */}
                            <th className="contracts-table__header-cell contracts-table__header-cell--document">
                                {t('DOCUMENT') || 'Document'}
                            </th>
                            {/* <th className="contracts-table__header-cell contracts-table__header-cell--duration">
                                {t('DURATION') || 'Duration'}
                            </th> */}
                            <th className="contracts-table__header-cell contracts-table__header-cell--actions">
                                {t('ACTIONS') || ''}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="contracts-table__tbody">
                        {contracts.length === 0 ? (
                            <tr className="contracts-table__empty-row">
                                <td colSpan={4} className="contracts-table__empty-cell">
                                    {t('NO_CONTRACTS') || 'No contracts available'}
                                </td>
                            </tr>
                        ) : (
                            contracts.map((contract) => (
                                <tr key={contract.id} className="contracts-table__body-row">
                                    {/* <td className="contracts-table__body-cell contracts-table__body-cell--contract-no">
                                        <span className="contracts-table__contract-no">
                                            {contract.contractNo}
                                        </span>
                                    </td> */}
                                    <td className="contracts-table__body-cell contracts-table__body-cell--document">
                                        <span className="contracts-table__document">
                                            <span className="contracts-table__document-icon" aria-hidden="true">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
                                                    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
                                                    <path d="M16 13H8" /><path d="M16 17H8" /><path d="M10 9H8" />
                                                </svg>
                                            </span>
                                            {t(contract.document)}
                                        </span>
                                    </td>
                                    {/* <td className="contracts-table__body-cell contracts-table__body-cell--duration">
                                        {contract.duration}
                                    </td> */}
                                    <td className="contracts-table__body-cell contracts-table__body-cell--actions">
                                        <div className="contracts-table__actions">
                                            <button
                                                className="contracts-table__action-btn contracts-table__action-btn--download"
                                                onClick={() => handleDownload(contract)}
                                                title={t('DOWNLOAD') || 'Download'}
                                                aria-label={t('DOWNLOAD') || 'Download'}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M8 1V10M8 10L5 7M8 10L11 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path d="M2 13H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                                </svg>
                                            </button>
                                            {/* <button
                                                className="contracts-table__action-btn contracts-table__action-btn--delete"
                                                onClick={() => onDelete?.(contract.id)}
                                                title={t('DELETE') || 'Delete'}
                                                aria-label={t('DELETE') || 'Delete'}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M2 4H14M6.5 7V12M9.5 7V12M3 4L4 13C4 13.5304 4.21071 14.0391 4.58579 14.4142C4.96086 14.7893 5.46957 15 6 15H10C10.5304 15 11.0391 14.7893 11.4142 14.4142C11.7893 14.0391 12 13.5304 12 13L13 4M5.5 4V3C5.5 2.73478 5.60536 2.48043 5.79289 2.29289C5.98043 2.10536 6.23478 2 6.5 2H9.5C9.76522 2 10.0196 2.10536 10.2071 2.29289C10.3946 2.48043 10.5 2.73478 10.5 3V4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </button> */}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
